import type { ErrorInfo } from 'react';
import React, { useCallback } from 'react';
import type { ChildrenProp } from '../common-types';
import { intl, showError } from '../../lib';
import { VBox } from '../layout';
import { BlankState } from './BlankState';
import { Button } from './Button';

/**
 * State of an {@link ErrorBoundary}.
 */
interface ErrorBoundaryState {
  /**
   * The {@link Error}.
   */
  error: null | Error;
  /**
   * Details of the error.
   */
  errorInfo: null | ErrorInfo;
}

/**
 * ErrorBoundary component which can be wrapped around any react component to display
 * possible thrown errors in all the components below it.
 *
 * Do not use this directly, it is already used by the POE!
 *
 * For details and limitations see {@link https://reactjs.org/docs/error-boundaries.html}.
 *
 */
export class ErrorBoundary extends React.Component<
  Readonly<Required<ChildrenProp>>,
  ErrorBoundaryState
> {
  /**
   * Constructor fo the {@link ErrorBoundary}.
   *
   * @param props the component props are simply only required children
   */
  constructor(props: Readonly<Required<ChildrenProp>>) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  /**
   * React lifecycle handler to handle errors in any components below.
   *
   * @param error the throw {@Link Error}
   * @param errorInfo details of the thrown error
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    showError(error);
    console.error(
      'Exception on the react render thread:',
      error,
      errorInfo.componentStack
    );

    // Catch errors in any components below and re-render with error message
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  /**
   * Render function of the {@link ErrorBoundary}.
   * @return the created {@link React.JSX.Element}
   */
  render(): React.JSX.Element {
    const { children, ...props } = this.props;
    return <>{this.state.error ? <FallbackUI {...props} /> : children}</>;
  }
}

/**
 * Component that acts as a fallback for the {@link ErrorBoundary}.
 */
function FallbackUI(
  props: Readonly<Record<string, unknown>>
): React.JSX.Element {
  const reload = useCallback(() => {
    window.location.reload();
  }, []);
  return (
    <VBox align="center" className="fallback-ui-vbox" {...props}>
      <VBox align="center" className="fallback-ui-vbox">
        <BlankState
          iconName="materialicon-report_problem"
          description={intl('error.CODED.BASE0000.msg')}
        />
        <Button
          variant="gray"
          text={intl('view.button.reload')}
          onClick={reload}
        />
      </VBox>
    </VBox>
  );
}
